<template>
  <div class="code-table">
    <code-form-modal
      :active.sync="modalForm.active"
      :code="modalForm.code"
      :firm-id="firmId"
      :contract-id="contractId"
      :type="filters.type"
      @submit="() => onFormSubmit()"
      @close="() => onFormClose()"
    />
    <b-field grouped>
      <base-select
        :service="$options.codeService"
        fetch-func="listTypes"
        label="Type"
        label-key="type"
        value-key="type"
        :value.sync="filters.type"
        :expanded="false"
        preselect-on-one
        hidden-on-one
        v-if="!type && !types"
      />

      <b-field label="Type" v-if="!type && types">
        <b-select v-model="filters.type">
          <option value>Please Select</option>
          <option v-for="value in types" :value="value" :key="value">
            {{ value }}
          </option>
        </b-select>
      </b-field>

      <base-select
        v-model="contractTypeId"
        :service="$options.contractService"
        fetch-func="listTypes"
        :fetch-params="{
          created_by_firm_id: firmId,
        }"
        label="Contract Type"
        v-if="mode == 'master'"
      />
    </b-field>

    <div class="buttons">
      <b-button
        :disabled="!filters.type"
        @click="() => (modalForm.active = true)"
      >
        Create
      </b-button>
      <b-button :disabled="!filters.type" @click="importCodes">Import</b-button>
    </div>

    <b-input
      v-model="search"
      placeholder="Search"
      icon-right="close-circle"
      icon-right-clickable
      @icon-right-click="
        () => (($refs.searchInput.newValue = ''), onTableSearch(''))
      "
      @input="(v) => onTableSearch(v)"
      ref="searchInput"
    ></b-input>

    <base-table
      :service="$options.codeService"
      fetch-func="all"
      :fetch-params="masterListParams"
      :columns="columns"
      checkable
      :checked.sync="codesChecked"
      :custom-is-checked="(a, b) => a.id == b.id"
      :is-row-checkable="isTableRowCheckable"
      ui-pagination
      @check="onTableCheck"
      @check-all="onTableCheckAll"
      ref="baseTable"
    >
      <template #rowAllContractsCheckbox="{ row }">
        <b-checkbox
          :value="isAllContractsCheckboxChecked(row)"
          :indeterminate="isAllContractsCheckboxChecked(row) == 'indeterminate'"
          :disabled="!contractTypeId"
          @input="(value) => onAllContractsCheckboxCheck(value, row)"
        />
      </template>
      <template #rowActions="{ row }">
        <b-button size="is-small" @click="() => editCode(row)">EDIT</b-button>
      </template>
    </base-table>
  </div>
</template>

<script>
import BaseTable from "@/components/partials/BaseTable";
import BaseSelect from "@/components/partials/BaseSelect";
import CodeFormModal from "@/components/Code/CodeFormModal";
import { CodeService } from "@/services/code.service";
import { ContractService } from "@/services/contract.service";
import { FirmService } from "@/services/firm.service";
import { TagService } from "@/services/tag.service";
import { JsonDenormalizer } from "@/helpers/JsonDenormalizer";
import FlatfileImporter from "flatfile-csv-importer";
import config from "@/config";

export default {
  codeService: CodeService,
  contractService: ContractService,
  props: {
    firmId: {
      type: [String, Number],
      required: true,
    },
    contractId: {
      type: [String, Number],
    },
    type: {
      type: String,
    },
    types: {
      type: Array,
    },
  },
  components: {
    BaseTable,
    BaseSelect,
    CodeFormModal,
  },
  data: () => ({
    mode: "master", // master (firm/contract_type) or contract
    contractTypeId: "",
    contractTypeContracts: [],
    filters: {
      type: "",
      archived: false,
    },
    columns: [
      {
        field: "group",
        label: "Group",
      },
      {
        field: "code",
        label: "Code",
      },
      {
        field: "name",
        label: "Name",
      },
      {
        field: "allContractsCheckbox",
        label: "",
        meta: {
          rowSlot: "rowAllContractsCheckbox",
        },
      },
      {
        field: "actions",
        label: "",
        meta: {
          rowSlot: "rowActions",
        },
      },
    ],
    codes: [],
    codesFiltered: [],
    codesChecked: [],
    search: "",
    modalForm: {
      active: false,
      code: null,
    },
    importer: null,
  }),
  computed: {
    user() {
      return this.$store.state.Auth.user;
    },
    // For table records (not to be confused with master mode)
    masterListParams() {
      return {
        firm_id: this.firmId,
        // firm_include_archived: this.filters.contractId ? undefined : 1,
        firm_include_archived: 1, // Archived master codes are displayed but disabled (contract mode)
        type: this.filters.type || undefined,
        includes: "tags",
      };
    },
    // For table checkbox (active records)
    activeListParams() {
      const shared = {
        type: this.filters.type || undefined,
      };
      if (this.mode == "master") {
        return {
          ...shared,
          firm_id: this.contractTypeId ? undefined : this.firmId,
          contract_type_tag_id: this.contractTypeId || undefined,
        }
      } else {
        return {
          ...shared,
          contract_id: this.contractId,
          firm_include_archived: 1, // Archived master codes are displayed but disabled (contract mode)
        };
      }
    },
  },
  created() {
    if (this.contractId) {
      this.mode = "contract";
    }
    if (this.type) {
      this.filters.type = this.type;
    }
  },
  mounted() {
    this.fetchCodes();
    // Initialize FlatFileImporter
    FlatfileImporter.setVersion(2);
    this.importer = new FlatfileImporter(
      config.flatfileLicense,
      config.codeFlatfileOptions
    );
    this.importer.setCustomer({
      userId: this.user.id,
      name: this.user.fullName,
      email: this.user.email,
    });
  },
  watch: {
    contractTypeId() {
      this.$nextTick(() => {
        this.fetchCodes();
        this.fetchContractTypeContracts();
      });
    },
    filters: {
      handler: function () {
        this.$nextTick(() => {
          this.fetchCodes();
        });
      },
      deep: true,
    },
    codesFiltered(codes) {
      this.$refs.baseTable.data = codes;
    },
  },
  methods: {
    // All master codes are displayed (including archived)
    // But archived master codes in contract mode are disabled (can't be checked)
    isTableRowCheckable(code) {
      if (this.mode == "master") return true;
      return !code.archived_at;
    },
    isAllContractsCheckboxChecked(code) {
      if (!this.contractTypeId) return false;

      let contractsHasCodeCount = 0;

      this.contractTypeContracts.forEach(contract => {
        // Check if code is assigned to contract
        if ((contract.codes || []).find(v => v.id == code.id)) {
          contractsHasCodeCount++;
        }
      });

      if (contractsHasCodeCount == this.contractTypeContracts.length) {
        return true;
      } else if (contractsHasCodeCount == 0) {
        return false;
      } else {
        return "indeterminate";
      }
    },
    filterCodes() {
      if (this.search) {
        this.codesFiltered = this.codes.filter((code) => {
          let bool = false;
          let search = new RegExp(this.search, "i"); // case insensitive search
          // Group
          bool = bool ? bool : `${code.group || ""}`.search(search) > -1;
          // Code
          bool = bool ? bool : `${code.code || ""}`.search(search) > -1;
          // Name
          bool = bool ? bool : `${code.name || ""}`.search(search) > -1;
          return bool;
        });
      } else {
        this.codesFiltered = this.codes;
      }
    },
    async fetchCodes() {
      if (this.filters.type) {
        const fetches = [
          this.$refs.baseTable.fetchData(),
          CodeService.all(this.activeListParams).then((response) =>
            JsonDenormalizer.denormalize(response)
          ),
        ];
        await Promise.all(fetches).then((responses) => {
          this.codes = responses[0];
          this.codesChecked = responses[1];
          this.filterCodes();
        });
      } else {
        this.codes = [];
        this.codesFiltered = [];
        this.codesChecked = [];
      }
    },
    editCode(code) {
      this.modalForm.code = code;
      this.modalForm.active = true;
    },
    async archiveRestoreCodes(mode = "archive", codes) {
      try {
        this.$refs.baseTable.loading = true;
        const ids = codes.map((v) => v.id);
        if (mode === "archive") {
          if (this.mode == "contract") {
            await ContractService.archiveCodes(this.contractId, {
              code_ids: ids,
            });
          } else {
            if (this.contractTypeId) {
              await TagService.archiveCodes(this.contractTypeId, { code_ids: ids });
            } else {
              await FirmService.archiveCodes(this.firmId, { code_ids: ids });
            }
          }
        } else {
          if (this.mode == "contract") {
            await ContractService.restoreCodes(this.contractId, {
              code_ids: ids,
            });
          } else {
            if (this.contractTypeId) {
              await TagService.restoreCodes(this.contractTypeId, { code_ids: ids });
            } else {
              await FirmService.restoreCodes(this.firmId, { code_ids: ids });
            }
          }
        }
        this.$buefy.toast.open({
          message: `Codes has been ${mode}d.`,
          type: "is-info",
          position: "is-top-right",
        });
      } catch (err) {
        console.error(err);
      } finally {
        this.$refs.baseTable.loading = false;
      }
    },
    async importCodes() {
      let records = await CodeService.all({
        ...this.masterListParams,
        includes: undefined,
      }).then((response) => JsonDenormalizer.denormalize(response));
      const codes = records.map((v) => v.code.toLowerCase());
      let localRecords = [];
      this.importer.registerRecordHook((record, key) => {
        // Reset local records
        if (key === 1) localRecords = [];

        let error = {};
        const recordCode = record.code.toLowerCase();
        if (codes.includes(recordCode)) {
          error.code = {
            info: [
              {
                message: "Duplicate: This code has been imported previously.",
                level: "error",
              },
            ],
          };
        } else {
          let localCodes = localRecords.map((v) => v.code.toLowerCase());
          if (localCodes.includes(recordCode)) {
            error.code = {
              info: [
                {
                  message:
                    "Duplicate: Import file contains multiple records of this code.",
                  level: "error",
                },
              ],
            };
          }
        }
        localRecords.push(record);
        return error;
      });
      await this.importer.requestDataFromUser().then(async (response) => {
        let data = response.data;
        // Modify data
        data = data.map((v) => {
          let m = JSON.parse(JSON.stringify(v));
          // Convert data columns
          m.data = {};
          Object.entries(m.$custom || {}).forEach(([k, v]) => {
            m.data[k.replace(/ /g, "_").toLowerCase()] = v;
          });
          delete m.$custom;
          return m;
        });
        try {
          await CodeService.import({
            firm_id: this.firmId,
            contract_id: this.contractId || undefined,
            type: this.filters.type,
            data,
          });
          this.importer.close();
          this.fetchCodes();
          this.$buefy.toast.open({
            message: "Codes successfully imported.",
            type: "is-info",
            position: "is-top-right",
          });
        } catch (err) {
          this.importer.displayError(err.message);
        }
      });
    },
    async fetchContractTypeContracts() {
      if (this.contractTypeId) {
        this.contractTypeContracts = await ContractService.all({
          type_tag_id: this.contractTypeId,
          includes: "codes",
        }).then(response => JsonDenormalizer.denormalize(response));
      } else {
        this.contractTypeContracts = [];
      }
    },
    onFormSubmit() {
      this.fetchCodes();
    },
    onFormClose() {
      this.modalForm.code = null;
    },
    onTableCheck(checked, row) {
      this.$nextTick(() => {
        if (row) {
          this.archiveRestoreCodes(
            !!this.codesChecked.find((c) => c.id == row.id)
              ? "restore"
              : "archive",
            [row]
          );
        }
      });
    },
    onTableCheckAll() {
      this.$nextTick(() => {
        const codesVisible = (
          this.$refs.baseTable.$refs.btable.visibleData || []
        ).map((c) => c.id);
        this.archiveRestoreCodes(
          this.codesChecked
            .filter((cc) => codesVisible.includes(cc.id))
            .filter((cc) => !!this.codesFiltered.find((cf) => cf.id == cc.id))
            .length
            ? "restore"
            : "archive",
          this.codesFiltered.filter((cc) => codesVisible.includes(cc.id))
        );
      });
    },
    onTableSearch: _.debounce(function () {
      if (this.search.length >= 3) {
        this.filterCodes();
        this.$refs.baseTable.pagination.perPage = 10000;
      }
      if (this.search.length == 0) {
        this.filterCodes();
        this.$refs.baseTable.pagination.perPage = 100;
      }
    }, 500),
    async onAllContractsCheckboxCheck(checked, row) {
      let requests = this.contractTypeContracts.map(contract => {
        if (checked) {
          return ContractService.restoreCodes(contract.id, {
            code_ids: [row.id]
          });
        } else {
          return ContractService.archiveCodes(contract.id, {
            code_ids: [row.id]
          });
        }
      });
      try {
        this.$refs.baseTable.loading = true;
        await Promise.all(requests);
        // Update
        await this.fetchContractTypeContracts();
      } catch (err) {
        console.error(err);
      } finally {
        this.$refs.baseTable.loading = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/Code/CodeTable.vue